import { useState } from "react";
import { useParams, Link, useNavigate } from "@/lib/router-compat";
import { useSEO } from "@/hooks/use-seo";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Building2,
  MapPin,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { ZoomableImage } from "@/components/ZoomableImage";
import { galleryPagesData } from "@/data/galleryPagesData";
import { projectsData } from "@/data/projects";

interface GalleryDetailProps {
  forcedSlug?: string;
}

export default function GalleryDetail({ forcedSlug }: GalleryDetailProps) {
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const slug = forcedSlug || routeSlug || "";
  const navigate = useNavigate();

  // Check in galleryPagesData first, then fallback to projectsData
  const galleryItem = galleryPagesData[slug];
  const projectItem = projectsData.find((p) => p.id === slug);

  const title =
    galleryItem?.title ||
    projectItem?.title ||
    slug.replace(/-/g, " ").toUpperCase();
  const category = galleryItem?.category || projectItem?.category || "Gallery";
  const images =
    galleryItem?.images ||
    projectItem?.images ||
    (projectItem?.img ? [projectItem.img] : []);
  const description =
    galleryItem?.description ||
    projectItem?.description ||
    "A selection of our best work at Gulf Fencing & Specialist Surfacing Est. W.L.L.";
  const client = galleryItem?.client || projectItem?.client;
  const location =
    galleryItem?.location || projectItem?.location || "Kingdom of Bahrain";
  const consultant = galleryItem?.consultant || projectItem?.consultant;
  const mainContractor =
    galleryItem?.mainContractor || projectItem?.mainContractor;

  useSEO(
    `${title} | Gulf Fencing & Specialist Surfacing Est. W.L.L.`,
    `${title} gallery showcase by Gulf Fencing & Specialist Surfacing Est. W.L.L., Bahrain.`,
  );

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);

  return (
    <div className="flex flex-col min-h-[100dvh] bg-background">
      {/* Header Banner */}
      <section className="relative h-[260px] md:h-[360px] flex items-center justify-center overflow-hidden bg-primary/95">
        <div
          className="absolute inset-0 z-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: images[0] ? `url('${images[0]}')` : undefined,
          }}
        />
        <div className="container relative z-10 mx-auto px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-secondary font-bold uppercase text-xs sm:text-sm tracking-widest mb-2 block">
              {category}
            </span>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-3 drop-shadow-md">
              {title}
            </h1>
            <p className="text-gray-200 text-xs sm:text-sm md:text-base max-w-xl mx-auto font-medium">
              A selection of our best work
            </p>
          </motion.div>
        </div>
      </section>

      {/* Navigation link under hero banner */}
      <div className="bg-muted/50 border-b border-border py-3">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-10 md:py-16 bg-white flex-1">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Metadata Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-10 bg-muted/30 p-6 rounded-xl border border-border">
            {client && (
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <span className="text-xs text-muted-foreground uppercase font-semibold block">
                    Client
                  </span>
                  <p className="text-sm font-bold text-foreground">{client}</p>
                </div>
              </div>
            )}
            {location && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <span className="text-xs text-muted-foreground uppercase font-semibold block">
                    Location
                  </span>
                  <p className="text-sm font-bold text-foreground">
                    {location}
                  </p>
                </div>
              </div>
            )}
            {consultant && (
              <div>
                <span className="text-xs text-muted-foreground uppercase font-semibold block">
                  Consultant
                </span>
                <p className="text-sm font-bold text-foreground">
                  {consultant}
                </p>
              </div>
            )}
            {mainContractor && (
              <div>
                <span className="text-xs text-muted-foreground uppercase font-semibold block">
                  Main Contractor
                </span>
                <p className="text-sm font-bold text-foreground">
                  {mainContractor}
                </p>
              </div>
            )}
          </div>

          {/* Description */}
          {description && (
            <div className="mb-10 text-center max-w-3xl mx-auto">
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                {description}
              </p>
            </div>
          )}

          {/* Image Gallery Grid */}
          <div>
            {images.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {images.map((imgUrl, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                    className="group relative rounded-xl overflow-hidden shadow-md border border-border aspect-4/3 cursor-pointer bg-muted"
                    onClick={() => {
                      setInitialSlide(idx);
                      setLightboxOpen(true);
                    }}
                  >
                    <img
                      src={imgUrl}
                      alt={`${title} Image ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-primary/20 transition-colors duration-300" />
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground">
                No images available for this showcase.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl bg-black/95 border-none p-0 overflow-hidden text-white flex items-center justify-center">
          <DialogTitle className="sr-only">
            {title} Gallery Lightbox
          </DialogTitle>
          <Carousel
            key={initialSlide}
            opts={{ startIndex: initialSlide, loop: true }}
            className="w-full max-w-[90vw] md:max-w-4xl mx-auto"
          >
            <CarouselContent>
              {images.map((imgUrl, idx) => (
                <CarouselItem
                  key={idx}
                  className="flex items-center justify-center p-4"
                >
                  <ZoomableImage
                    src={imgUrl}
                    alt={`${title} - image ${idx + 1}`}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-4 bg-black/50 text-white border-none hover:bg-black/80" />
            <CarouselNext className="right-4 bg-black/50 text-white border-none hover:bg-black/80" />
          </Carousel>
        </DialogContent>
      </Dialog>
    </div>
  );
}
